import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner, PageHeader } from '@/components/ui';
import { DataSourceItem, DataSourceType } from '../../../types/DataSource';
import { dataSourcesService } from '../../../services/dataSourcesService';
import DataSourcesTable, { DataSourceColumn } from '../components/DataSourcesTable';
import DataSourceModal, { DataSourceFormData, DataSourceFormField } from '../components/DataSourceModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { usePermissions } from '../../../hooks/usePermissions';

interface DataSourcesPageProps {
  title: string;
  source: DataSourceType;
  columns: DataSourceColumn[];
  fields: DataSourceFormField[];
  addButtonLabel: string;
  itemLabel: string;
}

const DataSourcesPage: React.FC<DataSourcesPageProps> = ({
  title,
  source,
  columns,
  fields,
  addButtonLabel,
  itemLabel
}) => {
  const [items, setItems] = useState<DataSourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DataSourceItem | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { hasPermission } = usePermissions();

  const canView = hasPermission('data_sources:view');
  const canEdit = hasPermission('data_sources:edit');
  const canDelete = hasPermission('data_sources:delete');

  const fetchDataSources = async () => {
    try {
      setLoading(true);
      const data = await dataSourcesService.getDataSources({
        source,
        search: searchTerm || undefined
      });
      setItems(data);
    } catch (error) {
      console.error('Error loading data sources:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchDataSources();
    }
  }, [source, searchTerm, canView]);

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item: DataSourceItem) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDeleteRequest = (item: DataSourceItem) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleSave = async (data: DataSourceFormData) => {
    const fieldKeys = new Set(fields.map(field => field.key));
    const payload = {
      code: data.code,
      name: data.name,
      type: fieldKeys.has('type') ? data.type : undefined,
      property: fieldKeys.has('property') ? data.property : undefined
    };

    if (selectedItem) {
      await dataSourcesService.updateDataSource(selectedItem.id, payload);
    } else {
      await dataSourcesService.createDataSource({
        source,
        ...payload
      });
    }
    await fetchDataSources();
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      setIsDeleting(true);
      await dataSourcesService.deleteDataSource(selectedItem.id);
      await fetchDataSources();
    } catch (error) {
      console.error('Error deleting data source:', error);
      alert('Failed to delete data source. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

  if (!canView) {
    return (
      <div className="tw-w-full tw-p-4">
        <h1>{title}</h1>
        <Alert variant="warning" className="tw-mt-3">You do not have access to this page.</Alert>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-p-4">
      <PageHeader
        title={title}
        actions={[
          {
            key: 'add',
            label: addButtonLabel,
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canEdit,
            onClick: handleAdd,
          },
        ]}
      >
        {/* This page searches inline rather than behind a toggle, so the field lives in the
            header's middle slot. Full-width on a phone, capped on desktop. */}
        <InputGroup className="tw-w-full sm:tw-max-w-[20rem]">
          <InputGroup.Text>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <Button variant="outline-primary" type="button" onClick={handleSearch}>
            Search
          </Button>
        </InputGroup>
      </PageHeader>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '200px' }}>
          <Spinner animation="border" role="status">
            <span className="tw-sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <DataSourcesTable
          items={items}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      <DataSourceModal
        visible={showModal}
        title={selectedItem ? `Edit ${itemLabel}` : `Add ${itemLabel}`}
        fields={fields}
        initialData={
          selectedItem
            ? {
                code: selectedItem.code,
                name: selectedItem.name,
                type: selectedItem.type,
                property: selectedItem.property
              }
            : undefined
        }
        onClose={() => setShowModal(false)}
        onSave={handleSave}
      />

      <DeleteConfirmModal
        visible={showDeleteModal}
        title="Delete Item"
        message={`Are you sure you want to delete this ${itemLabel.toLowerCase()}?`}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default DataSourcesPage;
